import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Send } from 'lucide-react';
import Swal from 'sweetalert2';
import { Helmet } from 'react-helmet-async';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QuotePage = () => {
  const [quoteForm, setQuoteForm] = useState({
    name: '', email: '', phone: '', service: '', origin: '', notes: ''
  });

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${API_URL}request-quote/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(quoteForm),
      });

      const result = await response.json();

      if (response.ok) {
        Swal.fire({
          icon: 'success',
          title: 'Quote Request Sent!',
          text: 'We will contact you within 24 hours.',
          confirmButtonColor: '#dc2626', // red-600
        });

        setQuoteForm({
          name: '',
          email: '',
          phone: '',
          service: '',
          origin: '',
          destination: '',
          notes: '',
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: result.notes || 'Submission failed.',
          confirmButtonColor: '#dc2626',
        });
      }
    } catch (error) {
      console.error("Error submitting quote:", error);
      Swal.fire({
        icon: 'error',
        title: 'Server Error',
        text: 'Please try again later.',
        confirmButtonColor: '#dc2626',
      });
    }
  };

  return (
    <div className="relative py-24 bg-gradient-to-br from-yellow-100 via-white to-red-100 overflow-hidden">
      <Helmet>
        <title>Request a Quote | S INFINITE EXPRESS PVT. LTD.</title>
        <meta
          name="description"
          content="Request a custom logistics quote from S INFINITE EXPRESS. We offer Part Truck Load (PTL), Full Truck Load (FTL), Express Delivery, Warehousing, and tailored logistics solutions across India."
        />
        <meta
          name="keywords"
          content="Logistics quote India, freight quote, S Infinite Express quote, PTL quote, FTL delivery estimate, express logistics India, warehousing quote, transportation pricing India, courier service estimate, shipping cost India, logistics rate request"
        />
        <meta name="author" content="S INFINITE EXPRESS PVT. LTD." />
        <meta property="og:title" content="Request a Quote | S INFINITE EXPRESS PVT. LTD." />
        <meta
          property="og:description"
          content="Get a fast and accurate logistics quote tailored to your needs. S INFINITE EXPRESS offers PTL, FTL, warehousing, and express services across India."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinfiniteexpress.com/request-quote" />
        <link rel="canonical" href="https://sinfiniteexpress.com/request-quote" />
      </Helmet>

      {/* Decorative Circles */}
      <div className="absolute -top-40 -left-32 w-96 h-96 bg-[#211f57]/10 rounded-full blur-3xl" />
      <div className="absolute -bottom-40 -right-32 w-96 h-96 bg-yellow-200/30 rounded-full blur-3xl" />
      <div className="max-w-5xl mx-auto px-4 relative z-10">

        {/* Quote Form */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="bg-gradient-to-r from-[#fff44f] via-[#fff86f] to-[#fffcb0] p-10 rounded-3xl border border-yellow-200 shadow-md"
        >
          <form onSubmit={handleQuoteSubmit} className="space-y-8">
            <h1 className="text-4xl sm:text-5xl font-extrabold text-[#211f57]">
              Request a Custom Quote
            </h1>
            <p className="mt-3 text-[#211f57] text-lg font-medium">
              Tailored shipping solutions by{' '}
              <span className="font-semibold text-red-600">S Infinite Express</span>
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {['name', 'email'].map((field) => (
                <div key={field}>
                  <label className="block text-sm font-semibold mb-2 capitalize text-[#211f57]">{field} *</label>
                  <input
                    type={field === 'email' ? 'email' : 'text'}
                    value={quoteForm[field]}
                    onChange={(e) => setQuoteForm({ ...quoteForm, [field]: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-4 focus:ring-yellow-200 focus:outline-none bg-white"
                  />
                </div>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold mb-2 text-[#211f57]">Phone</label>
                <input
                  type="tel"
                  value={quoteForm.phone}
                  onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-4 focus:ring-yellow-200 focus:outline-none bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2 text-[#211f57]">Service *</label>
                <select
                  value={quoteForm.service}
                  onChange={(e) => setQuoteForm({ ...quoteForm, service: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-4 focus:ring-yellow-200 focus:outline-none bg-white"
                >
                  <option value="">Select Service</option>
                  <option value="ltl">Less Than Truckload (LTL)</option>
                  <option value="ftl">Full Truck Load (FTL)</option>
                  <option value="3pl">Third-Party Load (3PL)</option>
                  <option value="express">Express Delivery</option>
                  <option value="international">International Shipping</option>
                  <option value="warehousing">Warehousing</option>
                </select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {['origin', 'destination'].map((field) => (
                <div key={field}>
                  <label className="block text-sm font-semibold mb-2 capitalize text-[#211f57]">{field} *</label>
                  <input
                    type="text"
                    placeholder="City, State"
                    value={quoteForm[field]}
                    onChange={(e) => setQuoteForm({ ...quoteForm, [field]: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-4 focus:ring-yellow-200 focus:outline-none bg-white"
                  />
                </div>
              ))}
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-[#211f57]">Additional Notes</label>
              <textarea
                rows="4"
                value={quoteForm.notes}
                onChange={(e) => setQuoteForm({ ...quoteForm, notes: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-4 focus:ring-yellow-200 focus:outline-none bg-white"
                placeholder="Tell us more about your shipment..."
              />
            </div>

            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              type="submit"
              className="px-6 py-3 rounded-xl text-white font-semibold text-lg shadow-lg transition bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 flex items-center justify-center gap-2 hover:shadow-xl"
            >
              <Send className="w-5 h-5" />
              Request Quote
            </motion.button>
          </form>
        </motion.div>

        {/* Benefits Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-12 grid md:grid-cols-2 gap-6"
        >
          {[
            {
              title: "Fast Response",
              text: "Get your quote within 24 hours."
            },
            {
              title: "Dedicated Support",
              text: "A logistics expert will assist you every step of the way."
            },
            {
              title: "Multiple Services",
              text: "PTL, FTL, Express, and Warehousing options."
            },
            {
              title: "Nationwide Reach",
              text: "Serving over 18,000+ pin codes across India."
            }
          ].map((item, idx) => (
            <div
              key={idx}
              className="flex items-start gap-4 p-5 rounded-2xl border border-yellow-200 bg-white hover:shadow-lg transition"
            >
              <div className="flex-shrink-0">
                <CheckCircle className="text-[#211f57] w-6 h-6 mt-1" />
              </div>
              <div>
                <h4 className="text-red-600 font-semibold mb-1">{item.title}</h4>
                <p className="text-gray-700 text-sm">{item.text}</p>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default QuotePage;
